import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Star, Trash2, Edit, Table2, MessageSquare, MapPin, Plus, Phone, Clock, UserCheck, CreditCard, UserX, Lock, LockOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Timer component for seated reservations
const SeatedTimer = ({ seatedAt }) => {
  const [elapsed, setElapsed] = useState('00:00:00');
  
  useEffect(() => {
    if (!seatedAt) return;
    
    const updateTimer = () => {
      const start = new Date(seatedAt);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;
      
      setElapsed(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [seatedAt]);
  
  // Check if over 2 hours (warning)
  const parts = elapsed.split(':');
  const hours = parseInt(parts[0]);
  const isWarning = hours >= 2;
  
  return (
    <span className={`font-mono text-sm ${isWarning ? 'text-red-400' : 'text-green-400'}`}>
      ⏱ {elapsed}
    </span>
  );
};

// Mini table plan component (read-only view) - shows multiple reservations per table
const MiniTablePlan = ({ tables, reservations, selectedDate }) => {
  const getTableStatus = (table) => {
    if (table.type === 'decor') return 'decor';
    const tableReservations = getAllReservationsForTable(table.id);
    if (tableReservations.length === 0) return 'free';
    
    // Check if any reservation is seated
    const hasSeated = tableReservations.some(r => r.status === 'seated');
    if (hasSeated) return 'seated';
    return 'reserved';
  };

  const getStatusColor = (status, reservationCount) => {
    switch (status) {
      case "free": return "#6B7280";
      case "reserved": return reservationCount > 1 ? "#F59E0B" : "#EF4444"; // Orange for multiple, red for single
      case "seated": return "#22C55E";
      case "decor": return "#4A4540";
      default: return "#D4AF37";
    }
  };

  const getAllReservationsForTable = (tableId) => {
    return reservations.filter(r => 
      r.table_id === tableId || 
      (r.table_ids && r.table_ids.includes(tableId))
    );
  };

  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4">
      <h3 className="text-lg font-serif text-gray-300 mb-3 flex items-center gap-2">
        <MapPin className="w-4 h-4 text-brand-gold" />
        Tafelplan - {selectedDate.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
      </h3>
      <div 
        className="relative w-full h-[250px] bg-[#0A0A0A] rounded border border-white/5"
        style={{
          backgroundImage: 'radial-gradient(circle, #222 1px, transparent 1px)',
          backgroundSize: '15px 15px'
        }}
      >
        {tables.map((table) => {
          if (table.type === 'decor') {
            return (
              <div
                key={table.id}
                className="absolute rounded border-2 border-dashed border-gray-700 flex items-center justify-center"
                style={{
                  left: `${(table.x / 1200) * 100}%`,
                  top: `${(table.y / 600) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  width: table.width ? `${parseInt(table.width) * 0.4}px` : '48px',
                  height: table.height ? `${parseInt(table.height) * 0.4}px` : '24px',
                  backgroundColor: '#2A2520'
                }}
              >
                <span className="text-[8px] text-gray-500">{table.name}</span>
              </div>
            );
          }

          const allReservations = getAllReservationsForTable(table.id);
          const status = getTableStatus(table);
          const isBarSeat = table.type === 'bar';
          const size = isBarSeat ? 20 : 36;
          const prefix = isBarSeat ? 'B' : 'T';
          const hasMultiple = allReservations.length > 1;

          let tooltipText = `${prefix}${table.number}: `;
          if (allReservations.length === 0) {
            tooltipText += 'Vrij';
          } else if (allReservations.length === 1) {
            const r = allReservations[0];
            tooltipText += `${r.guest_name || r.guest?.name || 'Gereserveerd'} (${r.time})`;
            if (r.status === 'seated') tooltipText += ' - AAN TAFEL';
          } else {
            tooltipText += `${allReservations.length} reserveringen: ` + allReservations.map(r => 
              `${r.time} - ${r.guest_name || r.guest?.name || 'Gereserveerd'}${r.status === 'seated' ? ' (ZITTEND)' : ''}`
            ).join(' | ');
          }

          return (
            <div
              key={table.id}
              className={`absolute flex flex-col items-center justify-center cursor-default transition-all hover:scale-110 ${
                table.shape === 'circular' ? 'rounded-full' : 'rounded'
              }`}
              style={{
                left: `${(table.x / 1200) * 100}%`,
                top: `${(table.y / 600) * 100}%`,
                transform: 'translate(-50%, -50%)',
                width: `${size}px`,
                height: `${size}px`,
                backgroundColor: status === 'seated' ? '#22C55E20' : hasMultiple ? '#F59E0B15' : '#1A1A1A',
                border: `2px solid ${getStatusColor(status, allReservations.length)}`
              }}
              title={tooltipText}
            >
              <span className="text-[9px] font-bold text-white">{prefix}{table.number}</span>
              {/* Show reservation count badge for multiple reservations */}
              {hasMultiple && !isBarSeat && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center text-[8px] font-bold text-black">
                  {allReservations.length}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-gray-500" />
          <span className="text-gray-400">Vrij</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-gray-400">Aan Tafel</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-gray-400">1 reservering</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span className="text-gray-400">2+ reserveringen</span>
        </div>
      </div>
    </div>
  );
};

// Interactive table plan for manual reservation form
const SelectableTablePlan = ({ tables, selectedTables, onToggleTable, selectedDate, selectedTime, existingReservations }) => {
  // Check if table is reserved at the specific time (with 2h30 buffer = 150 minutes)
  const isTableReservedAtTime = (tableId) => {
    if (!selectedTime) return false;
    
    const [selectedHour, selectedMin] = selectedTime.split(':').map(Number);
    const selectedMinutes = selectedHour * 60 + selectedMin;
    
    return existingReservations.some(r => {
      const tableIds = r.table_ids || (r.table_id ? [r.table_id] : []);
      if (!tableIds.includes(tableId)) return false;
      
      // Check time overlap (2h30 = 150 minute dining window)
      const [resHour, resMin] = r.time.split(':').map(Number);
      const resMinutes = resHour * 60 + resMin;
      
      // Reservations within 2h30 of each other conflict
      return Math.abs(selectedMinutes - resMinutes) < 150;
    });
  };

  const getReservationAtTime = (tableId) => {
    if (!selectedTime) return null;
    
    const [selectedHour, selectedMin] = selectedTime.split(':').map(Number);
    const selectedMinutes = selectedHour * 60 + selectedMin;
    
    return existingReservations.find(r => {
      const tableIds = r.table_ids || (r.table_id ? [r.table_id] : []);
      if (!tableIds.includes(tableId)) return false;
      
      const [resHour, resMin] = r.time.split(':').map(Number);
      const resMinutes = resHour * 60 + resMin;
      
      return Math.abs(selectedMinutes - resMinutes) < 150;
    });
  };

  return (
    <div className="bg-[#0A0A0A] rounded-lg p-3 border border-white/10">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-brand-gold" />
          Klik op tafels om te selecteren
        </h4>
        {selectedTables.length > 0 && (
          <span className="text-xs text-brand-gold">
            {selectedTables.length} tafel(s) geselecteerd
          </span>
        )}
      </div>
      <div 
        className="relative w-full h-[200px] bg-[#111] rounded border border-white/5"
        style={{
          backgroundImage: 'radial-gradient(circle, #1a1a1a 1px, transparent 1px)',
          backgroundSize: '12px 12px'
        }}
        data-testid="selectable-table-plan"
      >
        {tables.map((table) => {
          if (table.type === 'decor') {
            return (
              <div
                key={table.id}
                className="absolute rounded border border-dashed border-gray-700/50 flex items-center justify-center"
                style={{
                  left: `${(table.x / 1200) * 100}%`,
                  top: `${(table.y / 600) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  width: table.width ? `${parseInt(table.width) * 0.35}px` : '40px',
                  height: table.height ? `${parseInt(table.height) * 0.35}px` : '20px',
                  backgroundColor: '#1a1815'
                }}
              >
                <span className="text-[7px] text-gray-600">{table.name}</span>
              </div>
            );
          }

          const isBarSeat = table.type === 'bar';
          const size = isBarSeat ? 18 : 32;
          const prefix = isBarSeat ? 'B' : 'T';
          const isSelected = selectedTables.includes(table.id);
          const isReserved = isTableReservedAtTime(table.id);
          const reservation = getReservationAtTime(table.id);

          let bgColor = '#1A1A1A';
          let borderColor = '#4B5563'; // gray-600
          let cursor = 'pointer';

          if (isSelected) {
            bgColor = '#D4AF3730';
            borderColor = '#D4AF37';
          } else if (isReserved) {
            bgColor = '#EF444420';
            borderColor = '#EF4444';
            cursor = 'not-allowed';
          }

          const tooltipText = isReserved && reservation
            ? `${prefix}${table.number}: Bezet (${reservation.guest_name || 'Gereserveerd'} - ${reservation.time})`
            : `${prefix}${table.number}: ${table.capacity}p - Klik om te selecteren`;

          return (
            <div
              key={table.id}
              onClick={() => !isReserved && onToggleTable(table.id)}
              className={`absolute flex items-center justify-center transition-all ${
                table.shape === 'circular' ? 'rounded-full' : 'rounded'
              } ${!isReserved ? 'hover:scale-110 hover:brightness-125' : 'opacity-60'}`}
              style={{
                left: `${(table.x / 1200) * 100}%`,
                top: `${(table.y / 600) * 100}%`,
                transform: 'translate(-50%, -50%)',
                width: `${size}px`,
                height: `${size}px`,
                backgroundColor: bgColor,
                border: `2px solid ${borderColor}`,
                cursor: cursor
              }}
              title={tooltipText}
              data-testid={`select-table-${table.number}`}
            >
              <span className={`text-[8px] font-bold ${isSelected ? 'text-brand-gold' : isReserved ? 'text-red-400' : 'text-gray-300'}`}>
                {prefix}{table.number}
              </span>
              {isReserved && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-[6px] text-white">✕</span>
                </span>
              )}
              {isSelected && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-brand-gold rounded-full flex items-center justify-center">
                  <span className="text-[6px] text-black">✓</span>
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-2 text-[10px]">
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded border-2 border-gray-500 bg-[#1A1A1A]" />
          <span className="text-gray-500">Beschikbaar</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded border-2 border-brand-gold bg-brand-gold/20" />
          <span className="text-gray-500">Geselecteerd</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded border-2 border-red-500 bg-red-500/20" />
          <span className="text-gray-500">Bezet</span>
        </div>
      </div>
    </div>
  );
};

const ReservationsList = () => {
  const { t } = useTranslation();
  const [reservations, setReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [editingReservation, setEditingReservation] = useState(null);
  const [editForm, setEditForm] = useState({ time: "", staff_notes: "", party_size: 2 });
  
  // Closed dates for calendar
  const [closedDates, setClosedDates] = useState([]);
  const [closedDateStrings, setClosedDateStrings] = useState(new Set());
  
  // Manually closed dates
  const [manuallyClosedDates, setManuallyClosedDates] = useState(new Set());
  const [isSelectedDateClosed, setIsSelectedDateClosed] = useState(false);
  
  // Manual reservation form
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    time: "19:00",
    party_size: 2,
    allergies: "",
    notes: "",
    selected_tables: [],
    taken_by: ""  // Staff member who took the reservation
  });
  const [submittingManual, setSubmittingManual] = useState(false);

  // Close day modal state
  const [showCloseDayModal, setShowCloseDayModal] = useState(false);
  const [closeDayForm, setCloseDayForm] = useState({
    display_type: "volzet",
    custom_message: "",
    event_image: "",
    close_mode: "full_day", // "full_day" or "specific_times"
    closed_times: []  // Array of times to close
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [availableTimesForDay, setAvailableTimesForDay] = useState([]);

  // Fetch closed dates - refreshes on mount and when settings might have changed
  const fetchClosedDates = useCallback(async () => {
    try {
      const today = new Date();
      // Use local date formatting to avoid timezone issues
      const formatDate = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const startDate = formatDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
      // Fetch 365 days ahead
      const endDate = formatDate(new Date(today.getFullYear() + 1, today.getMonth(), 0));
      
      // Cache-busting for fresh data
      const cacheBuster = Date.now();
      const response = await axios.get(`${API}/closed-dates?start_date=${startDate}&end_date=${endDate}&_=${cacheBuster}`);
      const dates = response.data.closed_dates || [];
      setClosedDates(dates);
      setClosedDateStrings(new Set(dates.map(cd => cd.date)));
    } catch (error) {
      console.error("Error fetching closed dates:", error);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchClosedDates();
  }, [fetchClosedDates]);

  // Refresh when window gets focus (user returns from settings)
  useEffect(() => {
    const handleFocus = () => fetchClosedDates();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchClosedDates]);

  // Check if a date is closed
  const isDateClosed = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return closedDateStrings.has(dateStr);
  };

  // Get closed reason for a date
  const getClosedReason = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const closed = closedDates.find(cd => cd.date === dateStr);
    return closed?.reason || "Gesloten";
  };

  // Fetch manually closed dates
  const fetchManuallyClosedDates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/manually-closed-dates`);
      const dates = response.data.dates || [];
      setManuallyClosedDates(new Set(dates.map(cd => cd.date)));
    } catch (error) {
      console.error("Error fetching manually closed dates:", error);
    }
  }, []);

  // Check if selected date is manually closed
  useEffect(() => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    setIsSelectedDateClosed(manuallyClosedDates.has(dateStr));
  }, [selectedDate, manuallyClosedDates]);

  // Fetch available times for selected day when opening modal
  const fetchAvailableTimesForDay = useCallback(async () => {
    try {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios.post(`${API}/availability`, {
        date: dateStr,
        party_size: 2
      });
      
      setAvailableTimesForDay(response.data.available_times || []);
    } catch (error) {
      console.error("Error fetching available times:", error);
      setAvailableTimesForDay([]);
    }
  }, [selectedDate]);

  // Fetch times when modal opens
  useEffect(() => {
    if (showCloseDayModal) {
      fetchAvailableTimesForDay();
    }
  }, [showCloseDayModal, fetchAvailableTimesForDay]);

  // Toggle day closed/open
  const toggleDayClosed = async (forceClose = null) => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const shouldClose = forceClose !== null ? forceClose : !isSelectedDateClosed;
    
    // Determine closed_times based on mode
    const closedTimes = closeDayForm.close_mode === "specific_times" ? closeDayForm.closed_times : [];
    
    try {
      const response = await axios.post(`${API}/toggle-day-closed`, {
        date: dateStr,
        closed: shouldClose,
        reason: closeDayForm.display_type === "volzet" 
          ? (closedTimes.length > 0 ? `Volzet - ${closedTimes.length} uren` : "Volgeboekt - Handmatig gesloten")
          : closeDayForm.display_type === "event"
          ? `Event: ${closeDayForm.custom_message}`
          : "Gesloten",
        display_type: closeDayForm.display_type,
        custom_message: closeDayForm.custom_message,
        event_image: closeDayForm.event_image,
        closed_times: closedTimes
      });
      
      toast.success(response.data.message);
      setIsSelectedDateClosed(shouldClose && closedTimes.length === 0);
      setShowCloseDayModal(false);
      
      // Reset form
      setCloseDayForm({ display_type: "volzet", custom_message: "", event_image: "", close_mode: "full_day", closed_times: [] });
      
      // Refresh closed dates
      fetchClosedDates();
      fetchManuallyClosedDates();
    } catch (error) {
      console.error("Error toggling day:", error);
      toast.error("Fout bij wijzigen dagstatus");
    }
  };

  // Handle image upload for event
  const handleEventImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Afbeelding mag maximaal 2MB zijn");
      return;
    }
    
    setUploadingImage(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setCloseDayForm({...closeDayForm, event_image: reader.result});
      setUploadingImage(false);
    };
    reader.onerror = () => {
      toast.error("Fout bij laden afbeelding");
      setUploadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  // Handle opening the close day modal
  const handleOpenCloseDayModal = () => {
    if (isSelectedDateClosed) {
      // If already closed, just open it
      toggleDayClosed(false);
    } else {
      // Show modal to choose type
      setShowCloseDayModal(true);
    }
  };

  useEffect(() => {
    fetchManuallyClosedDates();
  }, [fetchManuallyClosedDates]);

  useEffect(() => {
    fetchReservations();
    fetchTables();
  }, [selectedDate]);

  const fetchTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`);
      setTables(response.data);
    } catch (error) {
      console.error("Error fetching tables:", error);
    }
  };

  const fetchReservations = async () => {
    try {
      setLoading(true);
      // Use local date formatting to avoid timezone issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const response = await axios.get(`${API}/reservations?date=${dateStr}`);
      
      // First fetch all tables
      let allTables = tables;
      if (tables.length === 0) {
        try {
          const tableResponse = await axios.get(`${API}/tables`);
          allTables = tableResponse.data;
          setTables(allTables);
        } catch (e) {
          console.error("Error loading tables:", e);
        }
      }
      
      // Fetch guest details for each reservation
      const reservationsWithGuests = await Promise.all(
        response.data.map(async (reservation) => {
          try {
            const guestResponse = await axios.get(`${API}/guests/${reservation.guest_id}`);
            
            // Get all assigned tables (for merged tables)
            let assignedTables = [];
            const tableIds = reservation.table_ids || (reservation.table_id ? [reservation.table_id] : []);
            
            for (const tid of tableIds) {
              const foundTable = allTables.find(t => t.id === tid);
              if (foundTable) {
                assignedTables.push(foundTable);
              }
            }
            
            return {
              ...reservation,
              guest: guestResponse.data,
              table: assignedTables[0] || null,
              tables: assignedTables
            };
          } catch (error) {
            console.error(`Error fetching guest ${reservation.guest_id}:`, error);
            return {
              ...reservation,
              guest: { name: 'Unknown', phone: '', email: '' },
              tables: []
            };
          }
        })
      );
      
      // Sort by time
      const sortedReservations = reservationsWithGuests.sort((a, b) => {
        return a.time.localeCompare(b.time);
      });
      
      setReservations(sortedReservations);
    } catch (error) {
      console.error("Error fetching reservations:", error);
      toast.error("Error loading reservations");
    } finally {
      setLoading(false);
    }
  };

  const handleEditReservation = (reservation) => {
    setEditingReservation(reservation);
    setEditForm({
      time: reservation.time,
      staff_notes: reservation.staff_notes || "",
      party_size: reservation.party_size || 2
    });
  };

  const saveReservationChanges = async () => {
    if (!editingReservation) return;
    
    try {
      await axios.put(`${API}/reservations/${editingReservation.id}`, {
        time: editForm.time,
        staff_notes: editForm.staff_notes,
        party_size: editForm.party_size
      });
      
      toast.success("Reservering bijgewerkt");
      if (editForm.time !== editingReservation.time && editingReservation.source === 'website') {
        toast.info("E-mail notificatie verzonden naar klant");
      }
      setEditingReservation(null);
      fetchReservations();
    } catch (error) {
      console.error("Error updating reservation:", error);
      toast.error("Fout bij bijwerken reservering");
    }
  };

  const assignTable = async (reservationId, tableId) => {
    try {
      await axios.put(`${API}/reservations/${reservationId}/assign-table?table_id=${tableId}`);
      toast.success("Tafel toegewezen");
      fetchReservations();
    } catch (error) {
      console.error("Error assigning table:", error);
      toast.error("Fout bij toewijzen tafel");
    }
  };

  const timeSlots = [
    "11:30", "11:45", "12:00", "12:15", "12:30", "12:45", "13:00", "13:15", "13:30", "13:45", "14:00", "14:15", "14:30",
    "17:30", "17:45", "18:00", "18:15", "18:30", "18:45", "19:00", "19:15", "19:30", "19:45", "20:00", "20:15", "20:30", "20:45", "21:00", "21:15", "21:30"
  ];

  // Reservation status actions
  const markSeated = async (reservationId) => {
    try {
      await axios.put(`${API}/reservations/${reservationId}/seat`);
      toast.success("Klant aan tafel gezet");
      fetchReservations();
    } catch (error) {
      console.error("Error marking seated:", error);
      toast.error("Fout bij aan tafel zetten");
    }
  };

  const markPaid = async (reservationId) => {
    try {
      await axios.put(`${API}/reservations/${reservationId}/paid`);
      toast.success("Betaald - tafel vrijgeschakeld");
      fetchReservations();
    } catch (error) {
      console.error("Error marking paid:", error);
      toast.error("Fout bij betalen");
    }
  };

  const markNoShow = async (reservationId) => {
    if (window.confirm('Weet u zeker dat deze klant een NO SHOW is? Dit wordt geregistreerd in de CRM.')) {
      try {
        await axios.put(`${API}/reservations/${reservationId}/no-show`);
        toast.success("NO SHOW geregistreerd");
        fetchReservations();
      } catch (error) {
        console.error("Error marking no-show:", error);
        toast.error("Fout bij registreren NO SHOW");
      }
    }
  };

  // Submit manual reservation
  const submitManualReservation = async () => {
    if (!manualForm.first_name || !manualForm.last_name || !manualForm.email || !manualForm.phone) {
      toast.error("Vul alle verplichte velden in");
      return;
    }
    
    try {
      setSubmittingManual(true);
      // Use local date formatting to avoid timezone issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios.post(`${API}/reservations/manual`, {
        first_name: manualForm.first_name,
        last_name: manualForm.last_name,
        email: manualForm.email,
        phone: manualForm.phone,
        date: dateStr,
        time: manualForm.time,
        party_size: manualForm.party_size,
        allergies: manualForm.allergies,
        notes: manualForm.notes,
        table_ids: manualForm.selected_tables,
        taken_by: manualForm.taken_by
      });
      
      toast.success("Reservering aangemaakt!");
      setShowManualForm(false);
      setManualForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        time: "19:00",
        party_size: 2,
        allergies: "",
        notes: "",
        selected_tables: [],
        taken_by: ""
      });
      fetchReservations();
    } catch (error) {
      console.error("Error creating manual reservation:", error);
      toast.error(error.response?.data?.detail || "Fout bij aanmaken reservering");
    } finally {
      setSubmittingManual(false);
    }
  };

  // Only show regular tables in dropdown (not decor)
  const selectableTables = tables.filter(t => t.type !== 'decor');

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="reservations-view">
      <div className="p-6 border-b border-white/10 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-serif text-gray-300" data-testid="reservations-title">{t('dashboard.reservations')}</h2>
          <p className="text-sm text-gray-400">Beheer reserveringen en wijs tafels toe</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={showManualForm} onOpenChange={setShowManualForm}>
            <DialogTrigger asChild>
              <Button
                className="bg-brand-gold text-black hover:bg-[#F0C74A]"
                data-testid="add-manual-reservation-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nieuwe Reservatie
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A1A1A] border-white/10 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-gray-200 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-brand-gold" />
                  Telefonische Reservatie Invoeren
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Voornaam *</Label>
                    <Input
                      value={manualForm.first_name}
                      onChange={(e) => setManualForm({...manualForm, first_name: e.target.value})}
                      placeholder="Jan"
                      className="bg-[#222222] border-white/20"
                      data-testid="manual-first-name"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Achternaam *</Label>
                    <Input
                      value={manualForm.last_name}
                      onChange={(e) => setManualForm({...manualForm, last_name: e.target.value})}
                      placeholder="Janssen"
                      className="bg-[#222222] border-white/20"
                      data-testid="manual-last-name"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Telefoonnummer *</Label>
                  <Input
                    value={manualForm.phone}
                    onChange={(e) => setManualForm({...manualForm, phone: e.target.value})}
                    placeholder="+32 471 123 456"
                    className="bg-[#222222] border-white/20"
                    data-testid="manual-phone"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">E-mailadres *</Label>
                  <Input
                    type="email"
                    value={manualForm.email}
                    onChange={(e) => setManualForm({...manualForm, email: e.target.value})}
                    placeholder="jan@voorbeeld.be"
                    className="bg-[#222222] border-white/20"
                    data-testid="manual-email"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Uur *</Label>
                    <Select value={manualForm.time} onValueChange={(value) => setManualForm({...manualForm, time: value})}>
                      <SelectTrigger className="bg-[#222222] border-white/20">
                        <SelectValue placeholder="Selecteer tijd" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#222222] border-white/20 max-h-[300px]">
                        {timeSlots.map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Aantal personen *</Label>
                    <Select value={String(manualForm.party_size)} onValueChange={(value) => setManualForm({...manualForm, party_size: parseInt(value)})}>
                      <SelectTrigger className="bg-[#222222] border-white/20">
                        <SelectValue placeholder="Aantal" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#222222] border-white/20">
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                          <SelectItem key={n} value={String(n)}>{n} {n === 1 ? 'persoon' : 'personen'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Allergenen</Label>
                  <Input
                    value={manualForm.allergies}
                    onChange={(e) => setManualForm({...manualForm, allergies: e.target.value})}
                    placeholder="bijv. lactose, noten"
                    className="bg-[#222222] border-white/20"
                    data-testid="manual-allergies"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Notities (intern)</Label>
                  <Textarea
                    value={manualForm.notes}
                    onChange={(e) => setManualForm({...manualForm, notes: e.target.value})}
                    placeholder="Interne opmerkingen..."
                    className="bg-[#222222] border-white/20 min-h-[80px]"
                    data-testid="manual-notes"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Reservatie aangenomen door</Label>
                  <Input
                    value={manualForm.taken_by}
                    onChange={(e) => setManualForm({...manualForm, taken_by: e.target.value})}
                    placeholder="Naam personeelslid"
                    className="bg-[#222222] border-white/20"
                    data-testid="manual-taken-by"
                  />
                </div>
                
                {/* Visual Table Selection */}
                <div>
                  <Label className="text-gray-300 mb-2 block">Tafels selecteren (optioneel)</Label>
                  <SelectableTablePlan
                    tables={tables}
                    selectedTables={manualForm.selected_tables || []}
                    onToggleTable={(tableId) => {
                      const selected = manualForm.selected_tables || [];
                      if (selected.includes(tableId)) {
                        setManualForm({...manualForm, selected_tables: selected.filter(id => id !== tableId)});
                      } else {
                        setManualForm({...manualForm, selected_tables: [...selected, tableId]});
                      }
                    }}
                    selectedDate={selectedDate}
                    selectedTime={manualForm.time}
                    existingReservations={reservations}
                  />
                  {(manualForm.selected_tables || []).length > 0 && (
                    <p className="text-xs text-brand-gold mt-2 font-medium">
                      ✓ Geselecteerd: {(manualForm.selected_tables || []).map(id => {
                        const t = tables.find(t => t.id === id);
                        return t ? `${t.type === 'bar' ? 'B' : 'T'}${t.number}` : id;
                      }).join(' + ')} 
                      (Totale capaciteit: {(manualForm.selected_tables || []).reduce((sum, id) => {
                        const t = tables.find(t => t.id === id);
                        return sum + (t ? t.capacity : 0);
                      }, 0)} personen)
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Laat leeg voor automatische toewijzing</p>
                </div>
                
                <div className="p-3 bg-[#222222] rounded-lg">
                  <p className="text-sm text-gray-400">
                    Datum: <span className="text-brand-gold font-semibold">
                      {selectedDate.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </p>
                </div>
                {/* Warning if date is closed */}
                {isDateClosed(selectedDate) && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-sm text-red-400 font-semibold">⚠️ WAARSCHUWING: Deze dag is GESLOTEN!</p>
                    <p className="text-xs text-red-400/70">{getClosedReason(selectedDate)}</p>
                    <p className="text-xs text-gray-400 mt-1">Weet u zeker dat u een reservering wilt invoeren op een gesloten dag?</p>
                  </div>
                )}
                <Button 
                  onClick={submitManualReservation} 
                  disabled={submittingManual}
                  className="w-full bg-brand-gold text-black hover:bg-[#F0C74A]"
                  data-testid="submit-manual-reservation"
                >
                  {submittingManual ? "Bezig..." : "Reservatie Aanmaken"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4">
              <h3 className="text-lg font-serif text-gray-300 mb-4">Selecteer Datum</h3>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                modifiers={{
                  closed: (date) => isDateClosed(date)
                }}
                modifiersClassNames={{
                  closed: "closed-day"
                }}
                className="rounded-md border border-white/20"
                data-testid="reservations-calendar"
              />
              {/* Closed date warning */}
              {isDateClosed(selectedDate) && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-400 font-semibold">⚠️ Let op: Deze dag is GESLOTEN</p>
                  <p className="text-xs text-red-400/70">{getClosedReason(selectedDate)}</p>
                </div>
              )}
              
              {/* Toggle day closed/open button */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <Button
                  onClick={handleOpenCloseDayModal}
                  variant="outline"
                  className={`w-full ${
                    isSelectedDateClosed 
                      ? 'border-green-500/50 text-green-400 hover:bg-green-500/10' 
                      : 'border-red-500/50 text-red-400 hover:bg-red-500/10'
                  }`}
                  data-testid="toggle-day-closed"
                >
                  {isSelectedDateClosed ? (
                    <>
                      <LockOpen className="w-4 h-4 mr-2" />
                      Dag weer openzetten
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Dag sluiten
                    </>
                  )}
                </Button>
                {isSelectedDateClosed && (
                  <p className="text-xs text-amber-400 mt-2 text-center">
                    Deze dag is gesloten voor nieuwe reservaties
                  </p>
                )}
              </div>

              {/* Close Day Modal */}
              <Dialog open={showCloseDayModal} onOpenChange={setShowCloseDayModal}>
                <DialogContent className="bg-[#1A1A1A] border-white/20 text-white max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-brand-gold font-serif text-xl">
                      Dag sluiten - {selectedDate.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-gray-300 mb-2 block">Type sluiting</Label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 p-3 bg-[#222222] rounded-lg cursor-pointer border border-transparent hover:border-amber-500/30 transition-colors">
                          <input
                            type="radio"
                            name="display_type"
                            value="volzet"
                            checked={closeDayForm.display_type === "volzet"}
                            onChange={(e) => setCloseDayForm({...closeDayForm, display_type: e.target.value})}
                            className="w-4 h-4 text-amber-500"
                          />
                          <div className="flex-1">
                            <span className="text-amber-400 font-medium">Volzet</span>
                            <p className="text-xs text-gray-400">Alle tafels zijn gereserveerd</p>
                          </div>
                          <span className="text-[10px] font-bold text-amber-500 bg-amber-500/20 px-2 py-1 rounded">VOLZET</span>
                        </label>
                        
                        <label className="flex items-center gap-3 p-3 bg-[#222222] rounded-lg cursor-pointer border border-transparent hover:border-purple-500/30 transition-colors">
                          <input
                            type="radio"
                            name="display_type"
                            value="event"
                            checked={closeDayForm.display_type === "event"}
                            onChange={(e) => setCloseDayForm({...closeDayForm, display_type: e.target.value})}
                            className="w-4 h-4 text-purple-500"
                          />
                          <div className="flex-1">
                            <span className="text-purple-400 font-medium">Event / Privé</span>
                            <p className="text-xs text-gray-400">Speciaal evenement met uitleg + afbeelding</p>
                          </div>
                          <span className="text-[10px] font-bold text-purple-500 bg-purple-500/20 px-2 py-1 rounded">EVENT</span>
                        </label>
                        
                        <label className="flex items-center gap-3 p-3 bg-[#222222] rounded-lg cursor-pointer border border-transparent hover:border-red-500/30 transition-colors">
                          <input
                            type="radio"
                            name="display_type"
                            value="closed"
                            checked={closeDayForm.display_type === "closed"}
                            onChange={(e) => setCloseDayForm({...closeDayForm, display_type: e.target.value})}
                            className="w-4 h-4 text-red-500"
                          />
                          <div className="flex-1">
                            <span className="text-red-400 font-medium">Gesloten</span>
                            <p className="text-xs text-gray-400">Restaurant is dicht (verlof, onderhoud, etc.)</p>
                          </div>
                          <span className="text-[10px] font-bold text-red-500 bg-red-500/20 px-2 py-1 rounded">DICHT</span>
                        </label>
                      </div>
                    </div>

                    {/* Time selection for Volzet */}
                    {closeDayForm.display_type === "volzet" && (
                      <div className="space-y-3">
                        <Label className="text-gray-300 mb-2 block">Welke uren volzet?</Label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-3 p-3 bg-[#222222] rounded-lg cursor-pointer border border-transparent hover:border-amber-500/30 transition-colors">
                            <input
                              type="radio"
                              name="close_mode"
                              value="full_day"
                              checked={closeDayForm.close_mode === "full_day"}
                              onChange={(e) => setCloseDayForm({...closeDayForm, close_mode: e.target.value, closed_times: []})}
                              className="w-4 h-4 text-amber-500"
                            />
                            <div className="flex-1">
                              <span className="text-amber-400 font-medium">Hele dag volzet</span>
                              <p className="text-xs text-gray-400">Geen enkele reservatie mogelijk</p>
                            </div>
                          </label>
                          
                          <label className="flex items-center gap-3 p-3 bg-[#222222] rounded-lg cursor-pointer border border-transparent hover:border-amber-500/30 transition-colors">
                            <input
                              type="radio"
                              name="close_mode"
                              value="specific_times"
                              checked={closeDayForm.close_mode === "specific_times"}
                              onChange={(e) => setCloseDayForm({...closeDayForm, close_mode: e.target.value})}
                              className="w-4 h-4 text-amber-500"
                            />
                            <div className="flex-1">
                              <span className="text-amber-400 font-medium">Specifieke uren volzet</span>
                              <p className="text-xs text-gray-400">Selecteer welke uren niet beschikbaar zijn</p>
                            </div>
                          </label>
                        </div>
                        
                        {closeDayForm.close_mode === "specific_times" && (
                          <div className="mt-4 p-4 bg-[#222222] rounded-lg border border-amber-500/20">
                            <p className="text-sm text-gray-300 mb-3">Selecteer de uren die volzet zijn:</p>
                            
                            {availableTimesForDay.length === 0 ? (
                              <p className="text-xs text-gray-500">Geen uren beschikbaar voor deze dag</p>
                            ) : (
                              <>
                                {/* Middag uren */}
                                {availableTimesForDay.filter(t => parseInt(t.split(':')[0]) < 17).length > 0 && (
                                  <div className="mb-4">
                                    <p className="text-xs text-gray-400 mb-2">☀️ Middag</p>
                                    <div className="flex flex-wrap gap-2">
                                      {availableTimesForDay.filter(t => parseInt(t.split(':')[0]) < 17).map(time => (
                                        <button
                                          key={time}
                                          type="button"
                                          onClick={() => {
                                            const times = closeDayForm.closed_times.includes(time)
                                              ? closeDayForm.closed_times.filter(t => t !== time)
                                              : [...closeDayForm.closed_times, time];
                                            setCloseDayForm({...closeDayForm, closed_times: times});
                                          }}
                                          className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                                            closeDayForm.closed_times.includes(time)
                                              ? 'bg-amber-500/30 border-amber-500 text-amber-300'
                                              : 'bg-[#1A1A1A] border-white/20 text-gray-400 hover:border-amber-500/50'
                                          }`}
                                        >
                                          {time}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Avond uren */}
                                {availableTimesForDay.filter(t => parseInt(t.split(':')[0]) >= 17).length > 0 && (
                                  <div>
                                    <p className="text-xs text-gray-400 mb-2">🌙 Avond</p>
                                    <div className="flex flex-wrap gap-2">
                                      {availableTimesForDay.filter(t => parseInt(t.split(':')[0]) >= 17).map(time => (
                                        <button
                                          key={time}
                                          type="button"
                                          onClick={() => {
                                            const times = closeDayForm.closed_times.includes(time)
                                              ? closeDayForm.closed_times.filter(t => t !== time)
                                              : [...closeDayForm.closed_times, time];
                                            setCloseDayForm({...closeDayForm, closed_times: times});
                                          }}
                                          className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                                            closeDayForm.closed_times.includes(time)
                                              ? 'bg-amber-500/30 border-amber-500 text-amber-300'
                                              : 'bg-[#1A1A1A] border-white/20 text-gray-400 hover:border-amber-500/50'
                                          }`}
                                        >
                                          {time}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {closeDayForm.closed_times.length > 0 && (
                                  <p className="text-xs text-amber-400 mt-3">
                                    {closeDayForm.closed_times.length} uur/uren geselecteerd als volzet
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {closeDayForm.display_type === "event" && (
                      <>
                        <div>
                          <Label className="text-gray-300 mb-2 block">Event beschrijving</Label>
                          <Textarea
                            value={closeDayForm.custom_message}
                            onChange={(e) => setCloseDayForm({...closeDayForm, custom_message: e.target.value})}
                            placeholder="Bijv: Privé huwelijksfeest, Personeelsfeest, Live muziek avond..."
                            className="bg-[#222222] border-white/20 text-white placeholder:text-gray-500"
                            rows={3}
                          />
                          <p className="text-xs text-gray-500 mt-1">Dit bericht wordt getoond aan klanten in een popup</p>
                        </div>
                        
                        <div>
                          <Label className="text-gray-300 mb-2 block">Event afbeelding (optioneel)</Label>
                          <div className="space-y-3">
                            {closeDayForm.event_image ? (
                              <div className="relative">
                                <img 
                                  src={closeDayForm.event_image} 
                                  alt="Event preview" 
                                  className="w-full max-h-64 object-contain rounded-lg border border-white/20 bg-black/20"
                                />
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  className="absolute top-2 right-2"
                                  onClick={() => setCloseDayForm({...closeDayForm, event_image: ""})}
                                >
                                  Verwijder
                                </Button>
                              </div>
                            ) : (
                              <label className="flex flex-col items-center justify-center h-32 bg-[#222222] border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-purple-500/50 transition-colors">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleEventImageUpload}
                                  className="hidden"
                                />
                                {uploadingImage ? (
                                  <span className="text-gray-400">Uploaden...</span>
                                ) : (
                                  <>
                                    <svg className="w-8 h-8 text-gray-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="text-sm text-gray-400">Klik om afbeelding te uploaden</span>
                                    <span className="text-xs text-gray-500">Max 2MB</span>
                                  </>
                                )}
                              </label>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    <div className="flex gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setShowCloseDayModal(false)}
                        className="flex-1 border-white/20 text-gray-300"
                      >
                        Annuleren
                      </Button>
                      <Button
                        onClick={() => toggleDayClosed(true)}
                        className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                        disabled={closeDayForm.display_type === "event" && !closeDayForm.custom_message.trim()}
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Dag sluiten
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <div className="mt-4 p-3 bg-[#222222] rounded-lg">
                <p className="text-sm text-gray-400">Reserveringen:</p>
                <p className="text-2xl font-semibold text-brand-gold">{reservations.length}</p>
                <p className="text-xs text-gray-500">{reservations.reduce((sum, r) => sum + r.party_size, 0)} gasten totaal</p>
              </div>
              {/* Legend */}
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                <span className="line-through text-red-400">XX</span>
                <span>= Gesloten</span>
              </div>
            </div>

            {/* Mini Table Plan */}
            <MiniTablePlan 
              tables={tables} 
              reservations={reservations} 
              selectedDate={selectedDate}
            />
          </div>

          <div className="lg:col-span-2 space-y-4">
            {loading ? (
              <div className="text-center py-8 text-gray-300">Laden...</div>
            ) : reservations.length === 0 ? (
              <div className="text-center py-8 text-gray-300" data-testid="no-reservations">Geen reserveringen voor deze datum</div>
            ) : (
              reservations.map((reservation, index) => (
                <div 
                  key={reservation.id} 
                  className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors"
                  data-testid={`reservation-card-${index}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="text-center bg-brand-gold/20 rounded-lg p-2 min-w-[60px]">
                        <p className="text-lg font-bold text-brand-gold" data-testid={`reservation-time-${index}`}>{reservation.time}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-200" data-testid={`guest-name-${index}`}>
                          {reservation.guest_name || reservation.guest?.name || 'Onbekend'}
                        </p>
                        <p className="text-sm text-gray-400">{reservation.party_size} personen</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Source badge - Online or Manual */}
                      <span className={`px-2 py-1 text-xs rounded ${
                        reservation.source === 'website' 
                          ? 'bg-cyan-500/20 text-cyan-400' 
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {reservation.source === 'website' ? '🌐 Online' : '📞 Handmatig'}
                      </span>
                      {/* Show who took the reservation for manual bookings */}
                      {reservation.source !== 'website' && reservation.taken_by && (
                        <span className="px-2 py-1 text-xs bg-gray-500/20 text-gray-400 rounded">
                          door {reservation.taken_by}
                        </span>
                      )}
                      {reservation.tables && reservation.tables.length > 0 && (
                        <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded" data-testid={`tables-badge-${index}`}>
                          {reservation.tables.map(t => 
                            `${t.type === 'bar' ? 'B' : 'T'}${t.number}`
                          ).join(' + ')}
                        </span>
                      )}
                      {reservation.reminder_sent && (
                        <span className="px-2 py-1 text-xs bg-purple-500/20 text-purple-400 rounded">
                          📧
                        </span>
                      )}
                      <span 
                        className={`px-2 py-1 text-xs rounded ${
                          reservation.status === 'seated' ? 'bg-green-500/20 text-green-400' :
                          reservation.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                          reservation.status === 'paid' ? 'bg-purple-500/20 text-purple-400' :
                          reservation.status === 'no_show' ? 'bg-orange-500/20 text-orange-400' :
                          reservation.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}
                      >
                        {reservation.status === 'seated' ? 'Aan Tafel' :
                         reservation.status === 'confirmed' ? 'Gereserveerd' :
                         reservation.status === 'paid' ? 'Betaald' :
                         reservation.status === 'no_show' ? 'NO SHOW' :
                         reservation.status}
                      </span>
                    </div>
                  </div>

                  {/* Timer for seated reservations */}
                  {reservation.status === 'seated' && reservation.seated_at && (
                    <div className="mb-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-green-400" />
                        <span className="text-green-400 font-semibold">Aan tafel sinds {new Date(reservation.seated_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <SeatedTimer seatedAt={reservation.seated_at} />
                    </div>
                  )}
                  
                  {/* Status Action Buttons - Only for confirmed/seated */}
                  {(reservation.status === 'confirmed' || reservation.status === 'seated') && (
                    <div className="mb-3 flex gap-2">
                      {reservation.status === 'confirmed' && (
                        <>
                          <Button
                            onClick={() => markSeated(reservation.id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white flex-1"
                            data-testid={`seat-btn-${index}`}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            Aan Tafel
                          </Button>
                          <Button
                            onClick={() => markNoShow(reservation.id)}
                            size="sm"
                            variant="outline"
                            className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                            data-testid={`noshow-btn-${index}`}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            NO SHOW
                          </Button>
                        </>
                      )}
                      {reservation.status === 'seated' && (
                        <Button
                          onClick={() => markPaid(reservation.id)}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white flex-1"
                          data-testid={`paid-btn-${index}`}
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          Betaald
                        </Button>
                      )}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4 text-sm border-t border-white/10 pt-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-gray-400">
                        <svg className="w-4 h-4 text-brand-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span>{reservation.guest?.phone || 'Geen'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <svg className="w-4 h-4 text-brand-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="truncate">{reservation.guest?.email || 'Geen'}</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      {reservation.allergies && (
                        <p className="text-orange-400 text-xs">⚠️ {reservation.allergies}</p>
                      )}
                      {reservation.special_requests && (
                        <p className="text-gray-400 text-xs">📝 {reservation.special_requests}</p>
                      )}
                    </div>
                  </div>
                  
                  {reservation.staff_notes && (
                    <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded">
                      <p className="text-xs text-yellow-400 flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        {reservation.staff_notes}
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-2">
                    {/* Edit Time & Notes Button */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditReservation(reservation)}
                          className="border-white/20 text-gray-300 hover:bg-white/10"
                          data-testid={`edit-reservation-${index}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Bewerken
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#1A1A1A] border-white/10">
                        <DialogHeader>
                          <DialogTitle className="text-gray-200">Reservering Bewerken</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div>
                            <Label className="text-gray-300">Aantal personen</Label>
                            <Select value={String(editForm.party_size)} onValueChange={(value) => setEditForm({...editForm, party_size: parseInt(value)})}>
                              <SelectTrigger className="bg-[#222222] border-white/20">
                                <SelectValue placeholder="Selecteer aantal" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#222222] border-white/20">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map(n => (
                                  <SelectItem key={n} value={String(n)}>{n} {n === 1 ? 'persoon' : 'personen'}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-gray-300">Tijd</Label>
                            <Select value={editForm.time} onValueChange={(value) => setEditForm({...editForm, time: value})}>
                              <SelectTrigger className="bg-[#222222] border-white/20">
                                <SelectValue placeholder="Selecteer tijd" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#222222] border-white/20 max-h-[300px]">
                                {timeSlots.map(time => (
                                  <SelectItem key={time} value={time}>{time}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {editingReservation?.source === 'website' && editForm.time !== editingReservation?.time && (
                              <p className="text-xs text-blue-400 mt-1">📧 Klant ontvangt e-mail bij tijdswijziging</p>
                            )}
                          </div>
                          <div>
                            <Label className="text-gray-300">Personeel Notities</Label>
                            <Textarea
                              value={editForm.staff_notes}
                              onChange={(e) => setEditForm({...editForm, staff_notes: e.target.value})}
                              placeholder="Interne opmerkingen..."
                              className="bg-[#222222] border-white/20 min-h-[100px]"
                            />
                          </div>
                          <Button onClick={saveReservationChanges} className="w-full bg-brand-gold text-black hover:bg-[#F0C74A]">
                            Opslaan
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Assign Table */}
                    <Select onValueChange={(tableId) => assignTable(reservation.id, tableId)}>
                      <SelectTrigger className="w-[140px] bg-transparent border-white/20 text-gray-300 h-9">
                        <Table2 className="w-4 h-4 mr-1" />
                        <SelectValue placeholder={
                          reservation.tables && reservation.tables.length > 0 
                            ? reservation.tables.map(t => `${t.type === 'bar' ? 'B' : 'T'}${t.number}`).join('+')
                            : "Tafel"
                        } />
                      </SelectTrigger>
                      <SelectContent className="bg-[#222222] border-white/20">
                        {selectableTables.map(table => (
                          <SelectItem key={table.id} value={table.id}>
                            {table.type === 'bar' ? 'B' : 'T'}{table.number} ({table.capacity}p)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Feedback Button */}
                    <Button
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/reservations/${reservation.id}/send-feedback`);
                          toast.success('Feedback e-mail verzonden!');
                        } catch (error) {
                          console.error('Error sending feedback:', error);
                          toast.error('Fout bij verzenden feedback e-mail');
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="border-brand-gold/50 text-brand-gold hover:bg-brand-gold/10"
                      data-testid={`send-feedback-${index}`}
                    >
                      <Star className="w-4 h-4 mr-1" />
                      Feedback
                    </Button>
                    
                    {/* Delete Button */}
                    <Button
                      onClick={async () => {
                        if (window.confirm('Weet u zeker dat u deze reservering wilt verwijderen?')) {
                          try {
                            await axios.delete(`${API}/reservations/${reservation.id}`);
                            setReservations(reservations.filter(r => r.id !== reservation.id));
                            toast.success('Reservering verwijderd');
                          } catch (error) {
                            console.error('Error deleting reservation:', error);
                            toast.error('Fout bij verwijderen reservering');
                          }
                        }
                      }}
                      variant="destructive"
                      size="sm"
                      data-testid={`delete-reservation-${index}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Verwijderen
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationsList;
